/**
 * 遠景鳥群：天上盤旋，繡像色，不跟著相機轉。
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { EMBROIDERY } from '../content/partLibrary'

interface BirdSpec {
  radius: number
  height: number
  speed: number
  phase: number
  color: string
}

const COLORS = [EMBROIDERY.ink, EMBROIDERY.azurite, EMBROIDERY.cinnabar, EMBROIDERY.ochre]

function makeFlock(): BirdSpec[] {
  const out: BirdSpec[] = []
  const rings = [
    { n: 16, radius: 78, height: 92, speed: 0.22 },
    { n: 12, radius: 118, height: 118, speed: 0.16 },
    { n: 8, radius: 158, height: 74, speed: -0.12 },
  ]
  for (const ring of rings) {
    for (let i = 0; i < ring.n; i++) {
      out.push({
        radius: ring.radius + (i % 3) * 4,
        height: ring.height + (i % 4) * 3,
        speed: ring.speed,
        phase: (i / ring.n) * Math.PI * 2,
        color: COLORS[i % COLORS.length],
      })
    }
  }
  return out
}

function Bird({ spec }: { spec: BirdSpec }) {
  const root = useRef<THREE.Group>(null)
  const wings = useRef<THREE.Group>(null)
  const bodyMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: spec.color,
    roughness: 0.9,
    metalness: 0,
    flatShading: true,
    side: THREE.DoubleSide,
  }), [spec.color])

  useFrame(({ clock }) => {
    if (!root.current) return
    const t = clock.elapsedTime * spec.speed + spec.phase
    const x = Math.cos(t) * spec.radius
    const z = Math.sin(t) * spec.radius
    const y = spec.height + Math.sin(t * 3.1) * 2.2
    root.current.position.set(x, y, z)
    root.current.rotation.y = t + Math.PI / 2
    if (wings.current) {
      const flap = Math.sin(clock.elapsedTime * 11 + spec.phase) * 0.55
      wings.current.rotation.z = flap
    }
  })

  return (
    <group ref={root} scale={2.4}>
      <mesh material={bodyMat} rotation={[0, 0, Math.PI / 2]}>
        <capsuleGeometry args={[0.045, 0.22, 3, 5]} />
      </mesh>
      <group ref={wings}>
        <mesh material={bodyMat} position={[0, 0.02, 0.16]} rotation={[0.15, 0.4, 0.2]}>
          <planeGeometry args={[0.28, 0.12]} />
        </mesh>
        <mesh material={bodyMat} position={[0, 0.02, -0.16]} rotation={[0.15, -0.4, -0.2]}>
          <planeGeometry args={[0.28, 0.12]} />
        </mesh>
      </group>
    </group>
  )
}

export function SkyFlock() {
  const flock = useMemo(() => makeFlock(), [])
  return (
    <group>
      {flock.map((spec, i) => (
        <Bird key={i} spec={spec} />
      ))}
    </group>
  )
}
